/**
 * Applies Word-native structure after drafting: lists (numbering/bullets), alignment, bold.
 * Agent paragraph indices match DocumentReader / non-empty paragraphs only.
 */

/* global Word */

import { log, logError } from '../../utils/logging';
import { formatOfficeJsError } from '../../utils/officeErrorFormat';
import { applyStandardOutlineNumbering, applyBulletAllLevels } from './wordListOutline';
import { prependRlmToHebrewParagraphs, textLooksHebrew, trySetSectionRtl } from './wordDocumentRtl';

async function syncWithLabel(context: Word.RequestContext, label: string): Promise<void> {
  try {
    await context.sync();
    log('[AgentStructureApplier] sync OK', label);
  } catch (e: unknown) {
    logError('[AgentStructureApplier] sync FAILED', label, formatOfficeJsError(e));
    throw e;
  }
}

export type StructureOp =
  | {
      op: 'numbered_list';
      /** Non-empty paragraph indices (agent scheme); sorted internally by Word position */
      agent_paragraph_indices: number[];
      numbering?: 'Arabic' | 'LowerLetter' | 'UpperLetter' | 'LowerRoman' | 'UpperRoman';
      levels?: number[];
    }
  | {
      op: 'bullet_list';
      agent_paragraph_indices: number[];
      bullet?: 'Solid' | 'Hollow' | 'Square';
    }
  | {
      op: 'align';
      agent_paragraph_index: number;
      alignment: 'Left' | 'Centered' | 'Right' | 'Justified';
    }
  | {
      op: 'bold_substring';
      agent_paragraph_index: number;
      substring: string;
    };

/** Strip leading "1. " / "2) " / "• " etc. so Word numbering isn't doubled */
function stripListPrefix(text: string): string {
  return text
    .replace(/^\s*\(?\d+[\.\)]\s*/, '')
    .replace(/^\s*\d+\s+/, '')
    .replace(/^\s*[א-ת][\.\)]\s*/, '')
    .replace(/^\s*[•\-\*]\s*/, '')
    .trim();
}

function wordIndexForAgentParagraph(texts: string[], agentIdx: number): number {
  let seen = -1;
  for (let wi = 0; wi < texts.length; wi++) {
    if ((texts[wi] || '').trim().length === 0) continue;
    seen++;
    if (seen === agentIdx) return wi;
  }
  return -1;
}

function mapNumbering(
  n: string | undefined
): 'Arabic' | 'LowerLetter' | 'UpperLetter' | 'LowerRoman' | 'UpperRoman' {
  const u = (n || 'Arabic').replace(/\s/g, '');
  const allowed = ['Arabic', 'LowerLetter', 'UpperLetter', 'LowerRoman', 'UpperRoman'] as const;
  if (allowed.includes(u as any)) return u as (typeof allowed)[number];
  return 'Arabic';
}

async function loadItemsAndTexts(context: Word.RequestContext): Promise<{ items: any[]; texts: string[] }> {
  try {
    const body = context.document.body;
    const pc = body.paragraphs;
    pc.load('items');
    await syncWithLabel(context, 'loadItems: paragraphs.items');
    const items = pc.items as any[];
    for (let i = 0; i < items.length; i++) {
      items[i].load(['text', 'isListItem']);
    }
    await syncWithLabel(context, 'loadItems: paragraph text+isListItem');
    const texts = items.map((p) => String(p.text ?? ''));
    return { items, texts };
  } catch (e: unknown) {
    logError('[AgentStructureApplier] loadItemsAndTexts failed', formatOfficeJsError(e));
    throw e;
  }
}

/**
 * @param ops - Operations in order. After `insert_paragraphs`, call this in a **later** tool step so Word reflects new paragraphs.
 */
export async function applyWordStructureOps(ops: StructureOp[]): Promise<{
  ok: boolean;
  applied: number;
  detail?: string;
}> {
  if (!Array.isArray(ops) || ops.length === 0) {
    return { ok: false, applied: 0, detail: 'No operations' };
  }

  try {
    log('[AgentStructureApplier] start', {
      opCount: ops.length,
      opsPreview: ops.map((o, i) => ({
        i,
        op: (o as StructureOp)?.op,
        keys: o && typeof o === 'object' ? Object.keys(o as object) : [],
      })),
    });

    let appliedSteps = 0;
    await Word.run(async (context) => {
      const bodyForRtl = context.document.body;
      bodyForRtl.load('text');
      await syncWithLabel(context, 'rtl-check: load body text');
      const rtlSample = String(bodyForRtl.text ?? '');
      let sectionRtlOk = false;
      if (textLooksHebrew(rtlSample)) {
        sectionRtlOk = await trySetSectionRtl(context);
      }

      let opIdx = 0;
      for (const raw of ops) {
        const op = raw as StructureOp;
        if (!op || typeof op !== 'object' || !('op' in op)) continue;
        opIdx += 1;
        log('[AgentStructureApplier] op begin', { opIdx, op: op.op });

        const { items, texts } = await loadItemsAndTexts(context);
        log('[AgentStructureApplier] context loaded', {
          opIdx,
          wordParagraphCount: items.length,
          nonEmptyAgentParagraphs: texts.filter((t) => t.trim().length > 0).length,
        });

        if (op.op === 'align') {
          const wi = wordIndexForAgentParagraph(texts, op.agent_paragraph_index);
          if (wi < 0) throw new Error(`align: agent paragraph ${op.agent_paragraph_index} not found`);
          items[wi].alignment = op.alignment;
          appliedSteps += 1;
          continue;
        }

        if (op.op === 'bold_substring') {
          const wi = wordIndexForAgentParagraph(texts, op.agent_paragraph_index);
          if (wi < 0) throw new Error(`bold_substring: agent paragraph ${op.agent_paragraph_index} not found`);
          const sub = op.substring.trim();
          if (!sub) continue;
          const range = items[wi].getRange();
          const found = range.search(sub, { matchCase: false, matchWholeWord: false });
          found.load('items');
          await syncWithLabel(context, `op${opIdx} bold_substring search`);
          const n = found.items?.length ?? 0;
          for (let j = 0; j < n; j++) {
            found.items[j].load('font');
          }
          await syncWithLabel(context, `op${opIdx} bold_substring load font`);
          for (let j = 0; j < n; j++) {
            found.items[j].font.bold = true;
          }
          appliedSteps += 1;
          continue;
        }

        if (op.op === 'numbered_list' || op.op === 'bullet_list') {
          const indices = [...(op.agent_paragraph_indices || [])].sort((a, b) => a - b);
          if (indices.length === 0) continue;
          const levels =
            op.op === 'numbered_list' && Array.isArray((op as any).levels) && (op as any).levels.length === indices.length
              ? (op as any).levels.map((x: unknown) => Math.max(0, Math.min(8, Number(x) || 0)))
              : indices.map(() => 0);

          const wordIndices: number[] = [];
          for (const ai of indices) {
            const wi = wordIndexForAgentParagraph(texts, ai);
            if (wi < 0) throw new Error(`list: agent paragraph ${ai} not found`);
            wordIndices.push(wi);
          }

          log('[AgentStructureApplier] list op resolved indices', {
            opIdx,
            kind: op.op,
            agentIndices: indices,
            wordIndices,
            levels,
            numbering: op.op === 'numbered_list' ? (op as any).numbering : undefined,
          });

          // Strip any typed numeric/bullet prefix so Word numbering isn't doubled
          for (const wi of wordIndices) {
            const original = texts[wi] ?? '';
            const stripped = stripListPrefix(original);
            if (stripped !== original) {
              items[wi].getRange().insertText(stripped, Word.InsertLocation.replace);
            }
          }

          for (const wi of wordIndices) {
            const para = items[wi];
            if (para.isListItem) {
              para.detachFromList();
            }
          }
          await syncWithLabel(context, `op${opIdx} list strip+detach`);

          const first = items[wordIndices[0]];
          const list = first.startNewList();
          await syncWithLabel(context, `op${opIdx} startNewList`);

          if (op.op === 'numbered_list') {
            const num = mapNumbering((op as any).numbering);
            log('[AgentStructureApplier] applyStandardOutlineNumbering', { opIdx, num });
            applyStandardOutlineNumbering(list, num);
          } else {
            const bullet = (((op as any).bullet as string) || 'Solid') as 'Solid' | 'Hollow' | 'Square';
            applyBulletAllLevels(list, bullet);
          }
          await syncWithLabel(context, `op${opIdx} setLevelNumbering/Bullet all levels`);

          list.load('id');
          await syncWithLabel(context, `op${opIdx} list.load id`);
          const listId = list.id;
          log('[AgentStructureApplier] list id', { opIdx, listId });

          const { items: items2 } = await loadItemsAndTexts(context);
          // startNewList() already attaches the first paragraph — attaching again throws GeneralException.
          for (let k = 1; k < wordIndices.length; k++) {
            const wi = wordIndices[k];
            items2[wi].attachToList(listId, levels[k] ?? 0);
          }
          await syncWithLabel(
            context,
            `op${opIdx} attachToList (${Math.max(0, wordIndices.length - 1)} rest after startNewList)`
          );
          appliedSteps += 1;
          continue;
        }
      }

      if (textLooksHebrew(rtlSample)) {
        if (!sectionRtlOk) {
          sectionRtlOk = await trySetSectionRtl(context);
        }
        if (!sectionRtlOk) {
          const n = await prependRlmToHebrewParagraphs(context);
          log('[AgentStructureApplier] RTL RLM fallback', { paragraphs: n });
        }
      }
    });

    log('[AgentStructureApplier] applied', { steps: appliedSteps });
    return { ok: true, applied: appliedSteps };
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    logError('[AgentStructureApplier] failed', { msg, office: formatOfficeJsError(e) });
    return { ok: false, applied: 0, detail: msg };
  }
}
